<script setup>
import {computed, getCurrentInstance, ref} from 'vue'
import store from '@/store'
import helpers from '@/lib/helpers'

const props = defineProps({
  // the serialized lead (needs _links.link_url and links.customer / links.vendor)
  lead: {type: Object, required: true},
  // optional: open straight to 'customer' or 'vendor'
  type: {type: String, default: 'customer'},
})

const proxy = getCurrentInstance().proxy
const type = ref(props.type)
const pick = ref(null)
const country = ref('')
const busy = ref(false)

const current = computed(() => (props.lead.links || {})[type.value] || null)
const listUrl = computed(() => `/api/${type.value}?limit=25`)
const searchUrl = computed(() => `/api/${type.value}?q=`)
const countryOptions = computed(() => [
  {value: '', text: 'Auto-detect from imported data (AI)'},
  ...(store.state.settings.defaults.countries || []),
])

const done = (result) => proxy.$root.$emit('submit', result)

const linkExisting = async () => {
  if (!pick.value) return
  busy.value = true
  try {
    done(await helpers.asyncAction(props.lead._links.link_url.href, 'POST', {data: {type: type.value, id: pick.value}}))
  } finally {
    busy.value = false
  }
}

const createFromLead = async () => {
  if (!await helpers.confirm(`Create a new ${type.value} from "${props.lead.name}" and link it? AI will fill company / contact / country from the imported data.`)) return
  busy.value = true
  try {
    let result = await helpers.asyncAction(props.lead._links.link_url.href, 'POST', {
      data: {type: type.value, create: true, country: country.value || undefined},
    })
    if (result.status !== 'W' && await helpers.confirm(`${type.value === 'customer' ? 'Customer' : 'Vendor'} created and linked. Move this lead to WON?`))
      result = await helpers.asyncAction(result._links.self_url.href, 'PUT', {data: {status: 'W'}})
    done(result)
  } finally {
    busy.value = false
  }
}

const unlink = async () => {
  if (!current.value) return
  busy.value = true
  try {
    done(await helpers.asyncAction(current.value.unlink_url.href, current.value.unlink_url.method))
  } finally {
    busy.value = false
  }
}
</script>

<template>
  <modal title="Link Lead" height="380px" :busy="busy">
    <div class="p-3">
      <p class="mb-3">
        <strong>{{ lead.name }}</strong>
        <span v-if="lead.company" class="text-muted"> · {{ lead.company }}</span>
        <span v-if="lead.email" class="text-muted d-block small">{{ lead.email }}<span v-if="lead.phone"> · {{ lead.phone }}</span></span>
      </p>

      <b-form-radio-group
          buttons size="sm" button-variant="outline-secondary" class="mb-3"
          v-model="type"
          @change="pick = null"
          :options="[{text: 'Customer', value: 'customer'}, {text: 'Vendor', value: 'vendor'}]"/>

      <b-alert v-if="current" show variant="info" class="py-2 px-3 small d-flex align-items-center">
        <span class="mr-auto">
          Linked to
          <b-link @click="$router.push(current.route); done()">{{ current.label || current.gli }}</b-link>
          <b-badge v-if="current.auto" variant="secondary" class="ml-1">auto</b-badge>
        </span>
        <b-button size="sm" variant="outline-danger" @click="unlink">Unlink</b-button>
      </b-alert>

      <b-form-group :label="'Link an existing ' + type" class="mb-2">
        <form-select-remote-search
            :key="type"
            placeholder="Search by company / name…"
            :url="listUrl"
            :search-url="searchUrl"
            key-text="company"
            key-value="id"
            v-model="pick"/>
      </b-form-group>
      <b-button size="sm" variant="primary" :disabled="!pick || busy" @click="linkExisting">
        Link selected
      </b-button>

      <hr/>

      <b-form-group label="Country for the new record" class="mb-2">
        <b-form-select size="sm" v-model="country" :options="countryOptions"/>
      </b-form-group>
      <b-button size="sm" variant="outline-primary" :disabled="busy" @click="createFromLead">
        <font-awesome-icon :icon="['fas', 'plus']" class="mr-1"/>
        Create new {{ type }} from this lead
      </b-button>
      <p class="text-muted small mt-1 mb-0">
        Company, contact name, website{{ type === 'customer' ? ', tax / VAT id' : ', account #' }} and country are read
        from the lead's original imported data by AI.
      </p>
    </div>

    <template #footer>
      <b-btn size="sm" variant="outline-secondary" class="px-3" @click="$root.$emit('submit')">Close</b-btn>
    </template>
  </modal>
</template>

<style scoped>

</style>
